import { useMemo, useState } from "react";
import { Loader2, Copy, Check, AlertCircle, CheckCircle2, Undo2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useRegionFilter } from "@/features/regions/hooks/use-region-filter";
import { getDashboardBaseUrl } from "@/lib/dashboard-url";
import {
  clearPreselection,
  generatePreselectLink,
  getPreselectUser,
  usePreselectProducts,
  usePreselectProfessionals,
  type GeneratePreselectLinkResult,
  type PreselectLinkMode,
  type PreselectUser,
} from "@/features/users/services/preselect-link";
import DecoratorProfessionalPicker, {
  type IndividualOption,
} from "@/features/users/ui/decorator-professional-picker";

// RH-884: link for a user who hasn't onboarded yet — they onboard and book their
// onboarding sessions with the professional picked here. Works with or without
// an existing account (blank user id = brand-new user).

const MODE_COPY: Record<PreselectLinkMode, string> = {
  saved_on_submission:
    "Saved on the user's onboarding submission — send them this link.",
  saved_on_form_submit:
    "Selection is saved when the user fills the onboarding form.",
  new_user:
    "New user: they fill the form on the login page, the selection is saved when they log in.",
};

function errorMessage(err: any, fallback: string): string {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join(", ");
  return typeof message === "string" && message ? message : fallback;
}

export default function DecoratorNewUserTab() {
  const { toast } = useToast();
  const filterRegion = useRegionFilter();

  const [userIdInput, setUserIdInput] = useState("");
  const [user, setUser] = useState<PreselectUser | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  const [productId, setProductId] = useState<string | null>(null);
  const [professionalId, setProfessionalId] = useState<string | null>(null);

  const [result, setResult] = useState<GeneratePreselectLinkResult | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Undo: the product whose pre-selection is awaiting confirmation / clearing.
  const [confirmUndoProductId, setConfirmUndoProductId] = useState<string | null>(null);
  const [undoingProductId, setUndoingProductId] = useState<string | null>(null);

  // A verified user's own region is authoritative — CT can't pick another.
  const region = user ? user.region ?? undefined : filterRegion;

  const resetFromProduct = () => {
    setProductId(null);
    setProfessionalId(null);
    setResult(null);
    setGenerateError(null);
  };

  const handleUserIdChange = (value: string) => {
    setUserIdInput(value);
    if (user || verifyError) {
      setUser(null);
      setVerifyError(null);
      resetFromProduct();
    }
  };

  const handleVerify = async () => {
    const trimmed = userIdInput.trim();
    if (!trimmed) return;
    setIsVerifying(true);
    setVerifyError(null);
    resetFromProduct();
    try {
      setUser(await getPreselectUser(trimmed));
    } catch (err) {
      setUser(null);
      setVerifyError(errorMessage(err, "User not found."));
    } finally {
      setIsVerifying(false);
    }
  };

  // Drop the saved professional so the user's onboarding sessions go back to the
  // round-robin pool, then reload the user card.
  const handleUndo = async (targetProductId: string) => {
    if (!user) return;
    setUndoingProductId(targetProductId);
    try {
      const { cleared } = await clearPreselection(user.id, targetProductId);
      toast({
        title: cleared ? "Pre-selection removed" : "Nothing to undo",
        description: cleared
          ? "The user is back on round robin for this product."
          : "The pre-selection was already removed or used by a purchase.",
      });
      setUser(await getPreselectUser(user.id));
      if (productId === targetProductId) setResult(null);
    } catch (err) {
      toast({
        title: "Couldn't undo",
        description: errorMessage(err, "Failed to remove the pre-selection."),
        variant: "destructive",
      });
    } finally {
      setUndoingProductId(null);
      setConfirmUndoProductId(null);
    }
  };

  const { data: regionProducts = [], isFetching: isLoadingProducts } =
    usePreselectProducts(region, !user);

  const productOptions = useMemo(
    () =>
      user
        ? user.products.map((p) => ({
            id: p.id,
            name: p.name,
            disabled: p.onboarded,
            note: p.onboarded ? "Already onboarded" : null,
          }))
        : regionProducts.map((p) => ({
            id: p.id,
            name: p.name,
            disabled: false,
            note: null as string | null,
          })),
    [user, regionProducts],
  );

  const { data: professionalsData, isFetching: isLoadingProfessionals } =
    usePreselectProfessionals({ productId, region, userId: user?.id });

  const professionalOptions: IndividualOption[] = useMemo(
    () =>
      (professionalsData?.professionals ?? []).map((p) => ({
        eventTypeId: p.id,
        disabled: !p.eligible,
        reason: p.reason,
        prof: {
          id: p.id,
          name: p.name,
          picture: p.picture,
          type: p.type,
          specialization: p.specialization,
          years_of_experience: p.years_of_experience,
        },
      })),
    [professionalsData],
  );
  const eligibleCount = professionalOptions.filter((o) => !o.disabled).length;

  const handleProductChange = (next: string) => {
    setProductId(next);
    setProfessionalId(null);
    setResult(null);
    setGenerateError(null);
  };

  const handleProfessionalChange = (next: string) => {
    setProfessionalId(next);
    setResult(null);
    setGenerateError(null);
  };

  const handleGenerate = async () => {
    if (!productId || !professionalId || !region) return;
    setIsGenerating(true);
    setGenerateError(null);
    setResult(null);
    try {
      setResult(
        await generatePreselectLink({
          product_id: productId,
          professional_id: professionalId,
          region,
          ...(user ? { user_id: user.id } : {}),
        }),
      );
    } catch (err) {
      const message = errorMessage(err, "Failed to generate the link.");
      setGenerateError(message);
      toast({ title: "Couldn't generate link", description: message, variant: "destructive" });
    } finally {
      setIsGenerating(false);
    }
  };

  const link = useMemo(() => {
    if (!result) return null;
    const qs = new URLSearchParams(result.query).toString();
    return `${getDashboardBaseUrl()}${result.path}${qs ? `?${qs}` : ""}`;
  }, [result]);

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Copied", description: "Link copied to clipboard." });
    } catch {
      toast({
        title: "Copy failed",
        description: "Select the link and copy it manually.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-sm text-gray-500">
        Build a link for a user who hasn't onboarded yet. They onboard and book their
        onboarding sessions with the professional you pick — no rebooking afterward.
      </p>

      {/* Step 1: User ID (optional) */}
      <Card className="p-4 space-y-3">
        <Label htmlFor="preselect-user-id">1. User ID (optional)</Label>
        <div className="flex gap-2">
          <Input
            id="preselect-user-id"
            placeholder="Leave blank for a brand-new user"
            value={userIdInput}
            onChange={(e) => handleUserIdChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleVerify()}
            className="font-mono"
          />
          <Button onClick={handleVerify} disabled={isVerifying || !userIdInput.trim()}>
            {isVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
          </Button>
        </div>

        {verifyError && (
          <div className="flex items-start gap-2 text-sm text-red-700 rounded-md border border-red-200 bg-red-50 p-3">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            {verifyError}
          </div>
        )}

        {user && (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 space-y-1 text-sm">
            <div className="flex items-center gap-2 text-green-800 font-medium">
              <CheckCircle2 className="h-4 w-4" />
              {user.full_name}
              {user.region && (
                <Badge className="bg-white text-gray-700 border-gray-200">
                  {user.region.toUpperCase()}
                </Badge>
              )}
            </div>
            <div className="text-gray-600">
              Phone: {user.phone_numbers?.join(", ") || "—"}
            </div>
            <div className="text-gray-600">Email: {user.emails?.join(", ") || "—"}</div>
            {!user.region && (
              <div className="text-yellow-700">
                ⚠ This user has no region — no link can be built.
              </div>
            )}
          </div>
        )}

        {user?.products
          .filter((p) => p.preselection)
          .map((p) => (
            <div
              key={p.id}
              className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm space-y-2"
            >
              <div className="text-blue-900">
                <span className="font-medium">
                  {p.preselection!.professionalName ?? "Unknown professional"}
                </span>{" "}
                is pre-selected for <span className="font-medium">{p.name}</span>
                {p.preselection!.at && (
                  <span className="text-blue-700">
                    {" "}
                    · saved {new Date(p.preselection!.at).toLocaleString()}
                  </span>
                )}
              </div>
              {confirmUndoProductId === p.id ? (
                <div className="flex items-center gap-2">
                  <span className="text-gray-700">
                    Send this user back to round robin for {p.name}?
                  </span>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleUndo(p.id)}
                    disabled={undoingProductId === p.id}
                  >
                    {undoingProductId === p.id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Confirm"
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmUndoProductId(null)}
                    disabled={undoingProductId === p.id}
                  >
                    Cancel
                  </Button>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmUndoProductId(p.id)}
                >
                  <Undo2 className="h-4 w-4 mr-1" />
                  Undo (back to round robin)
                </Button>
              )}
            </div>
          ))}

        {!user && !userIdInput.trim() && (
          filterRegion ? (
            <p className="text-xs text-gray-500">
              New user · region {filterRegion.toUpperCase()} (from the region filter)
            </p>
          ) : (
            <p className="text-xs text-amber-700">
              ⚠ Pick a region in the region filter to build a link for a new user.
            </p>
          )
        )}
      </Card>

      {/* Step 2 + 3: product + professional */}
      {region && (!userIdInput.trim() || user) && (
        <Card className="p-4 space-y-4">
          <div className="space-y-2">
            <Label>2. Product</Label>
            {isLoadingProducts ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading products…
              </div>
            ) : productOptions.length === 0 ? (
              <p className="text-sm text-gray-500">
                No products with onboarding set up in this region.
              </p>
            ) : (
              <Select value={productId ?? ""} onValueChange={handleProductChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {productOptions.map((p) => (
                    <SelectItem key={p.id} value={p.id} disabled={p.disabled}>
                      {p.name}
                      {p.note ? ` — ${p.note}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {productId && (
            <div className="space-y-2">
              <Label>3. Professional</Label>
              {isLoadingProfessionals ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading professionals…
                </div>
              ) : (
                <>
                  <DecoratorProfessionalPicker
                    options={professionalOptions}
                    value={professionalId}
                    onChange={handleProfessionalChange}
                  />
                  {professionalsData && (
                    <p className="text-xs text-gray-500">
                      {professionalsData.requiredSessions.length
                        ? `Needs an event type for: ${professionalsData.requiredSessions
                            .map((s) => s.name)
                            .join(", ")}`
                        : "No onboarding sessions configured for this product."}{" "}
                      ·{" "}
                      {professionalsData.routed
                        ? "checked against the bundle the user's answers route to"
                        : "checked against every onboarding bundle the product can route to"}{" "}
                      · {eligibleCount} eligible
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {productId && professionalId && (
            <Button onClick={handleGenerate} disabled={isGenerating}>
              {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate link"}
            </Button>
          )}

          {generateError && (
            <div className="flex items-start gap-2 text-sm text-red-700 rounded-md border border-red-200 bg-red-50 p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              {generateError}
            </div>
          )}
        </Card>
      )}

      {/* Result */}
      {result && link && (
        <Card className="p-4 space-y-2">
          <Label>Onboarding link</Label>
          <div className="flex gap-2">
            <Input readOnly value={link} className="font-mono text-xs" />
            <Button variant="outline" onClick={handleCopy}>
              {copied ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500">{MODE_COPY[result.mode]}</p>
        </Card>
      )}
    </div>
  );
}
